import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { ApiError } from '@greencandle/api-client'
import { AuthProvider, useAuth } from './auth'
import { Login } from './components/Login'
import { router } from './routes'
import './styles.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Portfolio figures change at most once a day for prices and FX; a minute of staleness
      // costs nothing and stops every tab focus refetching the whole dashboard.
      staleTime: 60_000,
      retry: (failureCount, error) => {
        // A 4xx is an answer, not a blip. Only retry what might succeed if asked again, and
        // never a 401 - the client already tried a token refresh before it surfaced.
        if (error instanceof ApiError && error.status < 500) {
          return false
        }
        return failureCount < 2
      },
    },
  },
})

function Root() {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="centered">
        <span className="muted">Checking your session…</span>
      </div>
    )
  }

  return user ? <RouterProvider router={router} /> : <Login />
}

const container = document.getElementById('root')
if (!container) {
  throw new Error('index.html is missing #root')
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Root />
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
)
